import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type ConfirmationOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
};

type ConfirmationContextValue = {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
};

type PendingConfirmation = ConfirmationOptions & {
  resolve: (confirmed: boolean) => void;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pendingRef = useRef<PendingConfirmation | null>(null);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (pending && !dialog.open) dialog.showModal();
    if (!pending && dialog.open) dialog.close();
  }, [pending]);

  function settle(confirmed: boolean) {
    const current = pendingRef.current;
    if (!current) return;

    pendingRef.current = null;
    setPending(null);
    current.resolve(confirmed);
  }

  function confirm(options: ConfirmationOptions) {
    if (pendingRef.current) return Promise.resolve(false);

    return new Promise<boolean>((resolve) => {
      const next = { ...options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <dialog
        ref={dialogRef}
        className="confirmation-dialog"
        aria-labelledby="confirmation-title"
        aria-describedby="confirmation-message"
        onCancel={(event) => {
          event.preventDefault();
          settle(false);
        }}
      >
        {pending && (
          <section className="confirmation-dialog-content">
            <h2 id="confirmation-title">{pending.title}</h2>
            <p id="confirmation-message">{pending.message}</p>
            <div className="confirmation-dialog-actions">
              <button className="button secondary" type="button" onClick={() => settle(false)}>
                {pending.cancelLabel ?? 'Cancel'}
              </button>
              <button
                className={pending.tone === 'danger' ? 'button danger' : 'button'}
                type="button"
                onClick={() => settle(true)}
              >
                {pending.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </section>
        )}
      </dialog>
    </ConfirmationContext.Provider>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within ConfirmationProvider.');
  }
  return context.confirm;
}
